/**
 * Task column filter interface for type-safe query keys
 * 
 * @property topicId - Filter by topic ID
 * @property status - Filter by column status
 * @property search - Filter by search term
 * @property sortBy - Field to sort by
 * @property sortDirection - Direction to sort (asc/desc)
 */
export interface TaskColumnFilters {
  topicId?: string;
  status?: string;
  search?: string;
  sortBy?: 'position' | 'name' | 'created';
  sortDirection?: 'asc' | 'desc';
}

/**
 * Query key factory for task columns
 * 
 * These keys are used to identify and organize React Query cache entries
 * related to task columns. Following TanStack Query best practices for structured keys.
 * 
 * Query keys are organized hierarchically to allow for selective invalidation:
 * - taskColumnKeys.all: Invalidates all task column-related queries
 * - taskColumnKeys.lists(): Invalidates all task column list queries
 * - taskColumnKeys.list(filters): Invalidates specific filtered lists
 * - taskColumnKeys.byTopic(topicId): Invalidates columns for a specific topic
 * - taskColumnKeys.details(): Invalidates all individual task column queries
 * - taskColumnKeys.detail(id): Invalidates a specific task column
 * 
 * Usage examples:
 * - taskColumnKeys.all - Base key for all task column data
 * - taskColumnKeys.lists() - Key for all task column lists
 * - taskColumnKeys.list({ topicId: 'topic-123' }) - Key for columns in a specific topic
 * - taskColumnKeys.detail('column-123') - Key for a specific task column
 * 
 * @example
 * // Invalidate all task column data
 * queryClient.invalidateQueries({ queryKey: taskColumnKeys.all });
 * 
 * @example
 * // Fetch task columns for a specific topic
 * useQuery({ queryKey: taskColumnKeys.byTopic('topic-123'), queryFn: fetchTaskColumns });
 */
export const taskColumnKeys = {
  /**
   * Base key for all task column-related queries
   * Invalidating this key will invalidate all task column queries
   */
  all: ['taskColumns'] as const,
  
  /**
   * Keys for all task column lists
   * Invalidating this key will invalidate all list queries but not detail queries
   * 
   * @returns Tuple of ['taskColumns', 'list']
   */
  lists: () => [...taskColumnKeys.all, 'list'] as const,
  
  /**
   * Key for filtered task column lists
   * Used for storing and retrieving specific filtered views of task columns
   * 
   * @param filters - Object containing filter criteria
   * @returns Tuple of ['taskColumns', 'list', {...filters}]
   * 
   * @example
   * // Get columns for a specific topic
   * taskColumnKeys.list({ topicId: 'topic-123' })
   * 
   * @example
   * // Get columns with a specific status
   * taskColumnKeys.list({ topicId: 'topic-123', status: 'doing' })
   * 
   * @example
   * // Get sorted columns
   * taskColumnKeys.list({ topicId: 'topic-123', sortBy: 'position', sortDirection: 'asc' })
   */
  list: (filters: TaskColumnFilters = {}) => [...taskColumnKeys.lists(), filters] as const,
  
  /**
   * Keys for all individual task column details
   * Invalidating this will invalidate all individual task column queries
   * 
   * @returns Tuple of ['taskColumns', 'detail']
   */
  details: () => [...taskColumnKeys.all, 'detail'] as const,
  
  /**
   * Key for a specific task column by ID
   * Used for storing and retrieving data about a specific task column
   * 
   * @param id - Task column ID
   * @returns Tuple of ['taskColumns', 'detail', id]
   * 
   * @example
   * // Get task column with ID "column-123"
   * taskColumnKeys.detail('column-123')
   * 
   * @example
   * // Invalidate a specific task column
   * queryClient.invalidateQueries({ queryKey: taskColumnKeys.detail('column-123') })
   */
  detail: (id: string) => [...taskColumnKeys.details(), id] as const,
  
  /**
   * Key for all task columns in a specific topic
   * Useful for invalidating all task columns when a topic changes
   * 
   * @param topicId - Topic ID
   * @returns Tuple of ['taskColumns', 'by-topic', topicId]
   * 
   * @example
   * // Invalidate all task columns in a topic
   * queryClient.invalidateQueries({ queryKey: taskColumnKeys.byTopic('topic-123') })
   */
  byTopic: (topicId: string) => [...taskColumnKeys.all, 'by-topic', topicId] as const,
};